import { useState } from 'react'
import { PlusIcon, TrashIcon } from '@heroicons/react/24/outline'
import type { DatasetColumn, FilterGroup, FilterCondition } from '../types'

interface FilterBuilderProps {
  columns: DatasetColumn[]
  value: FilterGroup | null
  onChange: (filter: FilterGroup | null) => void
}

type Operator = FilterCondition['op']

const OPERATORS: { value: Operator; label: string }[] = [
  { value: 'equals', label: 'Equals' },
  { value: 'not_equals', label: 'Not Equals' },
  { value: 'contains', label: 'Contains' },
  { value: 'not_contains', label: 'Not Contains' },
  { value: 'starts_with', label: 'Starts With' },
  { value: 'ends_with', label: 'Ends With' },
  { value: 'is_empty', label: 'Is Empty' },
  { value: 'is_not_empty', label: 'Is Not Empty' },
  { value: 'gt', label: 'Greater Than' },
  { value: 'gte', label: 'Greater or Equal' },
  { value: 'lt', label: 'Less Than' },
  { value: 'lte', label: 'Less or Equal' },
]

export default function FilterBuilder({ columns, value, onChange }: FilterBuilderProps) {
  const [conditions, setConditions] = useState<FilterCondition[]>(() => {
    if (!value) return []
    // Extract conditions from AND group
    if (value.and) {
      return value.and.filter((c): c is FilterCondition => 'column' in c)
    }
    return []
  })
  
  const [logic, setLogic] = useState<'and' | 'or'>('and')
  
  const addCondition = () => {
    const newCondition: FilterCondition = {
      column: columns[0]?.name || '',
      op: 'equals',
      value: '',
    }
    const updated = [...conditions, newCondition]
    setConditions(updated)
    updateFilter(updated, logic)
  }
  
  const removeCondition = (index: number) => {
    const updated = conditions.filter((_, i) => i !== index)
    setConditions(updated)
    updateFilter(updated, logic)
  }
  
  const updateCondition = (index: number, condition: FilterCondition) => {
    const updated = [...conditions]
    updated[index] = condition
    setConditions(updated)
    updateFilter(updated, logic)
  }
  
  const updateFilter = (conds: FilterCondition[], logicType: 'and' | 'or') => {
    if (conds.length === 0) {
      onChange(null)
      return
    }
    
    const filter: FilterGroup = logicType === 'and' 
      ? { and: conds }
      : { or: conds }
    onChange(filter)
  }
  
  const handleLogicChange = (newLogic: 'and' | 'or') => {
    setLogic(newLogic)
    updateFilter(conditions, newLogic)
  }
  
  const clearAll = () => {
    setConditions([])
    onChange(null)
  }
  
  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-4">
          <span className="text-sm font-medium text-gray-700">Match</span>
          <select
            value={logic}
            onChange={(e) => handleLogicChange(e.target.value as 'and' | 'or')}
            className="input w-auto text-sm"
          >
            <option value="and">All conditions (AND)</option>
            <option value="or">Any condition (OR)</option>
          </select>
        </div>
        <div className="flex gap-2">
          <button onClick={clearAll} className="btn-secondary text-sm px-3 py-1">
            Clear All
          </button>
          <button onClick={addCondition} className="btn-primary text-sm px-3 py-1">
            <PlusIcon className="h-4 w-4 mr-1" />
            Add Condition
          </button>
        </div>
      </div>
      
      {conditions.length === 0 ? (
        <p className="text-sm text-gray-500 text-center py-4">
          No filters applied. Click "Add Condition" to start filtering.
        </p>
      ) : (
        <div className="space-y-3">
          {conditions.map((condition, index) => (
            <div key={index} className="flex items-center gap-3">
              {/* Column select */}
              <select
                value={condition.column}
                onChange={(e) => updateCondition(index, { ...condition, column: e.target.value })}
                className="input w-40"
              >
                {columns.map((col) => (
                  <option key={col.id} value={col.name}>
                    {col.name}
                  </option>
                ))}
              </select>
              
              {/* Operator select */}
              <select
                value={condition.op}
                onChange={(e) => updateCondition(index, { ...condition, op: e.target.value as Operator })}
                className="input w-40"
              >
                {OPERATORS.map((op) => (
                  <option key={op.value} value={op.value}>
                    {op.label}
                  </option>
                ))}
              </select>
              
              {/* Value input (hidden for is_empty/is_not_empty) */}
              {!['is_empty', 'is_not_empty'].includes(condition.op) && (
                <input
                  type="text"
                  value={String(condition.value || '')}
                  onChange={(e) => updateCondition(index, { ...condition, value: e.target.value })}
                  placeholder="Value"
                  className="input flex-1"
                />
              )}
              
              {/* Remove button */}
              <button
                onClick={() => removeCondition(index)}
                className="p-2 text-gray-400 hover:text-red-500"
              >
                <TrashIcon className="h-5 w-5" />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
